// src/pages/ReviewQueue.jsx
//
// Review Queue — the lead reviewer's triage screen.
//
// Structure follows the queue prototype: four summary cards, a filter bar,
// one table, an assign dialog and a detail drawer. Styling does not — every
// colour and spacing value here comes from the MUI theme (src/theme.js) and
// the dashboard shell, not from the prototype's palette.
//
// Scope notes for anyone extending this:
//   * The prototype's review-cycle stepper is absent. review_events exists in
//     Mongo but no endpoint exposes it per draft, so a stepper here could only
//     re-render the status the badge already shows.
//   * The prototype's manager notes are absent. There is no collection behind
//     them; adding one is its own ticket.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, Button, CircularProgress, Checkbox, ListItemText, OutlinedInput, Stack,
} from '@mui/material';
import Sidebar from '../components/dashboard/Sidebar';
import AssignReviewerModal from '../components/dashboard/AssignReviewerModal';
import ReviewQueueDrawer from '../components/dashboard/ReviewQueueDrawer';
import { useAuth } from '../context/AuthContext';
import { rolesOf } from '../utils/roles';
import { API_BASE } from '../utils/api';
import '../styles/Dashboard.css';
import '../styles/Sidebar.css';

const STAFF_ROLES = ['lead_reviewer', 'admin'];
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Every row this endpoint returns is already status:"pending_review" — the
// backend hard-filters it. So "not completed" needs no filtering here; the
// cards slice the open set four ways instead of counting closed work.
//
// Exported for its own unit test: the stale/this-week split has a real
// boundary, and an undated draft (no lastEdited) deliberately ages to 0 and
// lands in "this week" rather than being reported as overdue.
export function summarise(rows) {
  const now = Date.now();
  // An unparseable date gives NaN, and NaN fails BOTH comparisons below, so
  // the row would drop out of stale and thisWeek alike -- a draft silently
  // missing from the triage counts. Treat it like an undated row instead, so
  // stale + thisWeek always sums to rows.length.
  const age = (r) => {
    if (!r.lastEdited) return 0;
    const ms = now - new Date(r.lastEdited).getTime();
    return Number.isFinite(ms) ? ms : 0;
  };
  return {
    unassigned: rows.filter((r) => !r.assignedTo).length,
    assigned: rows.filter((r) => !!r.assignedTo).length,
    stale: rows.filter((r) => age(r) > WEEK_MS).length,
    thisWeek: rows.filter((r) => age(r) <= WEEK_MS).length,
  };
}

function StatCard({ label, value, color }) {
  return (
    <Paper variant="outlined" data-testid="rq-stat-card" sx={{ p: 2, flex: '1 1 160px', minWidth: 160 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h4" data-testid="rq-stat-value" sx={{ color, fontWeight: 600, lineHeight: 1.2 }}>{value}</Typography>
    </Paper>
  );
}

// Multi-select filter. An empty selection means "no filter", which reads the
// same as the old "All" option without needing a sentinel value in the list.
function MultiFilter({ label, options, value, onChange, minWidth = 170 }) {
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(
          typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
        )}
        input={<OutlinedInput label={label} />}
        renderValue={(sel) => sel.join(', ')}
      >
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            <Checkbox checked={value.indexOf(o) > -1} size="small" />
            <ListItemText primary={o} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function ReviewQueue() {
  // Read identity from the auth context, not a one-shot localStorage read.
  // AuthContext writes the `user` key asynchronously after /api/me returns, so
  // a mount-time read of localStorage shows a genuine lead reviewer the logged
  // -out message on a cold load and never re-renders when the key lands.
  const { user, authReady } = useAuth();

  const accessToken = localStorage.getItem('rv_access_token');
  const headers = useMemo(() => ({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  }), [accessToken]);

  const [rows, setRows] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [genreFilter, setGenreFilter] = useState([]);
  const [reviewerFilter, setReviewerFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  // rolesOf reads whichever field carries the role — see src/utils/roles.js for
  // why checking `roles` alone is always false in production. This only decides
  // what is shown; @require_role on the endpoints is the real gate.
  const isStaff = rolesOf(user).some((r) => STAFF_ROLES.includes(r));

  const fetchAll = useCallback(async () => {
    try {
      const [queueRes, reviewersRes] = await Promise.all([
        fetch(`${API_BASE}/api/review-queue`, { credentials: 'omit', headers }),
        fetch(`${API_BASE}/api/admin/reviewers`, { credentials: 'omit', headers }),
      ]);
      setRows(queueRes.ok ? await queueRes.json() : []);
      setReviewers(reviewersRes.ok ? await reviewersRes.json() : []);
    } catch (e) {
      console.error('[ReviewQueue] fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!authReady) return;
    if (isStaff) fetchAll(); else setLoading(false);
  }, [fetchAll, isStaff, authReady]);

  const assign = async (reviewerId) => {
    if (!assignTarget) return;
    const targetId = assignTarget._id;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/review/${targetId}/assign`, {
        method: 'POST',
        credentials: 'omit',
        headers,
        body: JSON.stringify({ reviewerId: reviewerId || null }),
      });
      if (!res.ok) { alert('Could not assign this draft.'); return; }
      setAssignTarget(null);
      setDetailTarget((prev) => (prev && prev._id === targetId ? null : prev));
      // Refetch rather than patching state: assigning changes the reviewer's
      // activeReviews count too, and that number is the point of the dialog.
      await fetchAll();
    } catch {
      alert('Server error.');
    } finally {
      setBusy(false);
    }
  };

  const genres = useMemo(
    () => [...new Set(rows.map((r) => r.genre).filter(Boolean))].sort(),
    [rows],
  );
  const reviewerNames = useMemo(
    () => [...new Set(reviewers.map((r) => r.username).filter(Boolean))].sort(),
    [reviewers],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchSearch = !term
        || (r.title || '').toLowerCase().includes(term)
        || (r.authorName || '').toLowerCase().includes(term);
      const matchGenre = genreFilter.length === 0 || genreFilter.includes(r.genre);
      const matchReviewer = reviewerFilter.length === 0
        || reviewerFilter.includes(r.assignedUsername);
      const state = r.assignedTo ? 'Assigned' : 'Unassigned';
      const matchStatus = statusFilter.length === 0 || statusFilter.includes(state);
      return matchSearch && matchGenre && matchReviewer && matchStatus;
    });
  }, [rows, search, genreFilter, reviewerFilter, statusFilter]);

  const stats = useMemo(() => summarise(rows), [rows]);

  // Until auth settles there is nothing truthful to show: `user` is null both
  // for a signed-out visitor and for a signed-in one mid-handshake.
  if (!authReady) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }
  if (!user) {
    return <Box sx={{ p: 3 }}><Typography>Please log in to access the review queue.</Typography></Box>;
  }
  if (!isStaff) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Review Queue</Typography>
        <Typography color="text.secondary">This page is for lead reviewers.</Typography>
      </Box>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <h2 className="section-title">Review Queue</h2>

        {loading ? <CircularProgress /> : (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <StatCard label="Unassigned" value={stats.unassigned} color="warning.main" />
              <StatCard label="Assigned" value={stats.assigned} color="info.main" />
              <StatCard label="Waiting over 7 days" value={stats.stale} color="error.main" />
              <StatCard label="Submitted this week" value={stats.thisWeek} color="primary.main" />
            </Stack>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                placeholder="Search title or author…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240, flex: '1 1 240px' }}
              />
              <MultiFilter label="Genre" options={genres} value={genreFilter} onChange={setGenreFilter} />
              <MultiFilter label="Reviewer" options={reviewerNames} value={reviewerFilter} onChange={setReviewerFilter} />
              <MultiFilter label="Status" options={['Unassigned', 'Assigned']} value={statusFilter} onChange={setStatusFilter} minWidth={150} />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Title &amp; Author</strong></TableCell>
                    <TableCell><strong>Genre</strong></TableCell>
                    <TableCell><strong>Submitted</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Reviewer</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {rows.length === 0
                            ? 'Nothing is waiting for review.'
                            : 'No submissions match these filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filtered.map((r) => (
                    <TableRow key={r._id} hover data-testid="rq-row">
                      <TableCell onClick={() => setDetailTarget(r)} sx={{ cursor: 'pointer' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.title || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.authorName || 'Unknown author'}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.genre || '—'}</TableCell>
                      <TableCell>
                        {r.lastEdited ? new Date(r.lastEdited).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={r.assignedTo ? 'Assigned' : 'Unassigned'}
                          color={r.assignedTo ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell data-testid="rq-row-reviewer">{r.assignedUsername || '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" data-testid="rq-assign-btn" onClick={() => setAssignTarget(r)}>
                          {r.assignedTo ? 'Reassign' : 'Assign'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <AssignReviewerModal
          open={!!assignTarget}
          draft={assignTarget}
          reviewers={reviewers}
          busy={busy}
          onAssign={assign}
          onUnassign={() => assign(null)}
          onClose={() => setAssignTarget(null)}
        />

        <ReviewQueueDrawer
          draft={detailTarget}
          headers={headers}
          onClose={() => setDetailTarget(null)}
          onAssignClick={(d) => { setDetailTarget(null); setAssignTarget(d); }}
        />
      </main>
    </div>
  );
}
